import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Chip,
  Grid,
  Card,
  CardContent,
  Button,
  Divider
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Help as HelpIcon,
  Create as CreateIcon,
  Event as EventIcon,
  EmojiEvents as TrophyIcon,
  AccountCircle as AccountIcon,
  Security as SecurityIcon,
  Support as SupportIcon,
  Article as ArticleIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const FAQ = () => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const faqCategories = [
    {
      title: "Getting Started",
      icon: <HelpIcon />,
      color: "primary",
      faqs: [
        {
          question: "How do I create an account?",
          answer: "Simply click the 'Sign In' button in the top right corner and choose 'Sign in with Google'. We use Google OAuth for secure and easy authentication. No need to remember another password!"
        },
        {
          question: "Is the platform free to use?",
          answer: "Yes! The Short Story Platform is completely free to use. You can read stories, write your own, participate in contests, and engage with the community at no cost."
        },
        {
          question: "Do I need to be a professional writer to join?",
          answer: "Not at all! Our platform welcomes writers of all skill levels, from beginners to published authors. Whether you're just starting your writing journey or you're an experienced storyteller, you'll find a supportive community here."
        },
        {
          question: "What makes this platform different from other writing sites?",
          answer: "We focus specifically on short stories with features like writing contests, community engagement, word count tracking, and a clean, distraction-free reading experience. Plus, we offer both public sharing and private writing options."
        }
      ]
    },
    {
      title: "Writing & Publishing",
      icon: <CreateIcon />,
      color: "secondary",
      faqs: [
        {
          question: "How do I write and publish a story?",
          answer: "Click 'Write Story' in the navigation menu. You can write your story using our rich text editor, add tags, set visibility (public/private), and save as draft or publish immediately. Published stories appear in the community feed."
        },
        {
          question: "Can I save my work as a draft?",
          answer: "Yes! You can save your stories as drafts and return to edit them later. Drafts are private and only visible to you until you choose to publish them."
        },
        {
          question: "What's the difference between public and private stories?",
          answer: "Public stories are visible to all users and appear in the community feed. Private stories are only visible to you and won't appear in public listings. You can change visibility settings at any time."
        },
        {
          question: "Is there a word limit for stories?",
          answer: "We focus on short stories, so we recommend keeping stories under 5,000 words for the best reading experience. However, there's no strict limit - write what feels right for your story!"
        },
        {
          question: "Can I edit my story after publishing?",
          answer: "Yes, you can edit your published stories at any time. Simply go to 'My Stories' and click the edit button on any of your stories."
        },
        {
          question: "How do tags work?",
          answer: "Tags help readers discover your stories. Add relevant tags like genre (sci-fi, romance, mystery), themes, or mood. Good tagging helps your stories reach the right audience."
        }
      ]
    },
    {
      title: "Writing Contests",
      icon: <EventIcon />,
      color: "success",
      faqs: [
        {
          question: "How do writing contests work?",
          answer: "Contests have specific themes, word limits, and deadlines. Join a contest, write a story that fits the theme, and submit before the deadline. After the contest closes, judges evaluate submissions and select winners. You'll receive email notifications about contest status updates if you've joined."
        },
        {
          question: "How do I join a contest?",
          answer: "Go to the 'Events' page, find an active contest, and click 'Join Contest'. Once joined, you can submit a story that meets the contest requirements. You'll automatically receive email reminders about upcoming deadlines."
        },
        {
          question: "Can I submit multiple stories to one contest?",
          answer: "Typically, you can submit one story per contest. Check the specific contest rules for details, as some special contests may allow multiple submissions."
        },
        {
          question: "Who judges the contests?",
          answer: "Contests are judged by our community moderators, experienced writers, and designated judges. The judging process includes detailed evaluation criteria and scoring. Judges can leave feedback comments on submissions during the judging phase."
        },
        {
          question: "What do winners receive?",
          answer: "Winners receive recognition on the platform, their stories are featured prominently, and they earn bragging rights in our community! Winners are announced through email notifications and featured in contest results."
        },
        {
          question: "Can I see past contest results?",
          answer: "Yes! Visit the 'Results' page to see winners and finalists from all published contest results. You can read the winning stories and see detailed judging feedback and scores."
        },
        {
          question: "Do I get notified about contest updates?",
          answer: "Yes! If you join a contest, you'll receive email notifications about important updates like deadline reminders, judging completion, and results announcements."
        },
        {
          question: "What happens to old contests?",
          answer: "Completed contests are automatically archived after a period of time to keep the platform organized. Archived contests and their results remain accessible but are moved to a separate archive section."
        }
      ]
    },
    {
      title: "Community & Interaction",
      icon: <ArticleIcon />,
      color: "info",
      faqs: [
        {
          question: "How do I find stories to read?",
          answer: "Browse the 'Stories' page to see all public stories. You can filter by tags, search by title or author, or check out featured stories on the homepage (available when logged in). Use the search function to find specific content."
        },
        {
          question: "Can I comment on stories?",
          answer: "Yes! Registered users can comment on public stories. Comments help build community and provide feedback to authors. You can also reply to other comments to create discussions. Please keep comments constructive and respectful."
        },
        // {
        //   question: "Why don't I see featured stories on the homepage?",
        //   answer: "Featured stories are only displayed to logged-in users. If you're not signed in, you won't see the featured stories section. This helps us provide a personalized experience for our community members."
        // },
        {
          question: "How do I follow my favorite authors?",
          answer: "While we don't have a formal 'follow' system yet, you can bookmark authors' profiles and check back for new stories. This feature is on our roadmap for future updates!"
        },
        {
          question: "Can I share stories outside the platform?",
          answer: "Anyone who is logged in to platform can access the storeis. However, please respect authors' rights and always credit the original author when sharing their work."
        },
        {
          question: "Do I get email notifications?",
          answer: "Yes! You'll receive email notifications for important events like contest deadlines, judging results, and account-related updates. Email notifications help keep you engaged with the community and informed about your submissions. Check with your email provider if they block emails coming from our domain."
        }
      ]
    },
    {
      title: "Admin & Moderation",
      icon: <SecurityIcon />,
      color: "secondary",
      faqs: [
        {
          question: "How do I become an admin or judge?",
          answer: "Admin and judge roles are assigned by existing administrators. If you're interested in helping moderate the community or judge contests, reach out through our support channels to express your interest."
        },
        {
          question: "What can admins do?",
          answer: "Admins can manage users, moderate content, create and manage contests, assign judges, publish contest results, and access platform analytics. They also receive notifications when new users register."
        },
        {
          question: "How does the judging system work?",
          answer: "Judges are assigned to specific contests and can access a dedicated judging interface. They can score submissions, leave detailed feedback, and collaborate with other judges. The admin panel provides tools for managing the entire judging process."
        },
        {
          question: "Can I see detailed contest results?",
          answer: "Yes! Contest results include detailed scoring, judge feedback, and rankings. This transparency helps writers understand how their work was evaluated and learn from the feedback."
        }
      ]
    },
    {
      title: "Account & Profile",
      icon: <AccountIcon />,
      color: "warning",
      faqs: [
        {
          question: "How do I update my profile?",
          answer: "Click on your profile icon in the top right corner and select 'Profile'. You can update your display name, bio, and other profile information."
        },
        {
          question: "Can I change my display name?",
          answer: "Yes, you can change your display name in your profile settings. This is the name that appears on your stories and comments."
        },
        {
          question: "How do I delete my account?",
          answer: "If you need to delete your account, go to your profile settings and look for the account deletion option. Please note that this action cannot be undone."
        },
        {
          question: "What happens to my stories if I delete my account?",
          answer: "When you delete your account, your stories are also removed from the platform. Make sure to save any stories you want to keep before deleting your account."
        }
      ]
    },
    {
      title: "Privacy & Security",
      icon: <SecurityIcon />,
      color: "error",
      faqs: [
        {
          question: "How is my data protected?",
          answer: "We use industry-standard security measures including encrypted data storage on AWS, secure authentication via Google OAuth, HTTPS everywhere, and regular security updates. Your privacy and data security are our top priorities."
        },
        {
          question: "What information do you collect?",
          answer: "We only collect information necessary to provide our services: your Google account information (name, email), your stories, comments, contest participation, and usage analytics to improve the platform. We never sell your data."
        },
        {
          question: "Can other users see my email address?",
          answer: "No, your email address is private and never displayed to other users. Only your display name is visible to the community. We use your email only for important notifications and account management."
        },
        {
          question: "How do I report inappropriate content?",
          answer: "If you encounter inappropriate content or behavior, please contact our support team immediately. We take community safety seriously and will investigate all reports promptly. Admins have tools to moderate content and manage user accounts."
        },
        {
          question: "What email notifications will I receive?",
          answer: "You may receive notifications for contest deadlines, judging results, account updates, and important platform announcements. All emails are sent from verified domains and you can manage your notification preferences."
        },
        {
          question: "Is my writing secure?",
          answer: "Yes! All your stories are stored securely in encrypted databases with regular backups. Your drafts and published stories are protected with enterprise-grade security measures on AWS infrastructure."
        }
      ]
    }
  ];

  const quickLinks = [
    { title: "Write Your First Story", action: () => navigate('/stories/new'), icon: <CreateIcon /> },
    { title: "Browse Stories", action: () => navigate('/stories'), icon: <ArticleIcon /> },
    { title: "Join a Contest", action: () => navigate('/events'), icon: <EventIcon /> },
    { title: "View Contest Results", action: () => navigate('/results'), icon: <TrophyIcon /> }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
          Frequently Asked Questions
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
          Everything you need to know about the Short Story Platform
        </Typography>
        
        {/* Quick Links */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Quick Actions
          </Typography>
          <Grid container spacing={2} justifyContent="center">
            {quickLinks.map((link, index) => (
              <Grid item key={index}>
                <Button
                  variant="outlined"
                  startIcon={link.icon}
                  onClick={link.action}
                  sx={{ borderRadius: 2 }}
                >
                  {link.title}
                </Button>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Box>

      {/* FAQ Categories */}
      {faqCategories.map((category, categoryIndex) => (
        <Box key={categoryIndex} sx={{ mb: 4 }}>
          {/* Category Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Box sx={{ mr: 2, color: `${category.color}.main` }}>
              {category.icon}
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              {category.title}
            </Typography>
            <Chip 
              label={`${category.faqs.length} questions`} 
              size="small" 
              color={category.color}
              sx={{ ml: 2 }}
            />
          </Box>

          {/* FAQ Items */}
          {category.faqs.map((faq, faqIndex) => (
            <Accordion
              key={`${categoryIndex}-${faqIndex}`}
              expanded={expanded === `panel${categoryIndex}-${faqIndex}`}
              onChange={handleChange(`panel${categoryIndex}-${faqIndex}`)}
              sx={{ mb: 1 }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`panel${categoryIndex}-${faqIndex}bh-content`}
                id={`panel${categoryIndex}-${faqIndex}bh-header`}
              >
                <Typography sx={{ fontWeight: 'medium' }}>
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      ))}

      {/* Contact Support Section */}
      <Divider sx={{ my: 6 }} />
      
      <Card sx={{ textAlign: 'center', p: 4 }}>
        <CardContent>
          <SupportIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Still Have Questions?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Can't find what you're looking for? Our support team is here to help!
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<SupportIcon />}
            onClick={() => navigate('/support')}
            sx={{ mr: 2 }}
          >
            Contact Support
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate('/privacy')}
          >
            Privacy Policy
          </Button>
        </CardContent>
      </Card>

      {/* Tips Section */}
      <Box sx={{ mt: 6 }}>
        <Paper sx={{ p: 4, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
          <Typography variant="h6" gutterBottom>
            💡 Pro Tips for New Users
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                • Start with a short story to get familiar with the platform
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                • Use relevant tags to help readers discover your work
              </Typography>
              <Typography variant="body2">
                • Engage with the community by reading and commenting on other stories
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                • Join contests to challenge yourself and gain exposure
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                • Save drafts frequently while writing longer pieces
              </Typography>
              <Typography variant="body2">
                • Check out contest results for inspiration and learning
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </Container>
  );
};

export default FAQ;
